import { BalanceData, FusionRequest, GearItem, GearTarget, PlayerProfile, Rarity, raiseCostRange } from "@highmarch/domain";
import {
  FusableGroup, FusionCandidates, FusionPreview, ItemCardView, feedCandidates, feedPreview, fusableGroups, fusionPreview, itemCard,
} from "@highmarch/app";
import { RARITY_LABEL } from "../gearUi";

/**
 * The Forge's view models (gear v2, docs/GEAR_V2.md §8): the words and numbers the item card's forge box, the Fusion
 * screen and the Feed panel show, built on the app's read models (forgeView.ts) so a preview is what the action does.
 * Pure over the profile: no DOM (itemForge.ts, fusion.ts and feed.ts draw them).
 */

// ------------------------------------------------------------------------------------------------ the item's level

/**
 * Who sets an item's cap: the tower (or the Fortress) wearing it, or, unworn, the best tower (tower gear) or the
 * Fortress. `name` is the owner ("Ballista"); `phrase` how a sentence names it ("your best tower, Ballista").
 */
export function capOwner(profile: PlayerProfile, item: GearItem): { readonly name: string; readonly phrase: string; readonly level: number; readonly towerIndex: number | null } {
  const B = profile.balance;
  const fortress = { name: "the Fortress", phrase: "the Fortress", level: profile.baseLevel, towerIndex: null };
  if (item.equippedTo !== null) {
    const o = item.equippedTo;
    if (o.target === GearTarget.Base) return fortress;
    const name = B.towers[o.towerIndex]?.name ?? "its tower";
    return { name, phrase: name, level: profile.towerLevels[o.towerIndex], towerIndex: o.towerIndex };
  }
  if (B.gear.slots[item.slot].target === GearTarget.Base) return fortress;
  let best = -1;
  profile.towerLevels.forEach((l, i) => { if (!B.towers[i].isBase && (best < 0 || l > profile.towerLevels[best])) best = i; });
  if (best < 0) return { name: "a tower", phrase: "your best tower", level: 1, towerIndex: null };
  return { name: B.towers[best].name, phrase: `your best tower, ${B.towers[best].name}`, level: profile.towerLevels[best], towerIndex: best };
}

/** The forge box on the item card: its level against the cap, the next level, Raise and Raise max. */
export interface RaiseBox {
  readonly level: number;
  readonly cap: number;
  /** 0…1: how far toward the cap (the bar). */
  readonly fill: number;
  readonly atCap: boolean;
  /** "Can rise to Lv 40, Ballista's level", or at the cap "Capped at Ballista's level · upgrade Ballista to go higher". */
  readonly capLine: string;
  readonly capTower: number | null;
  /** The next level's cost, stats and power (null at the cap). */
  readonly next: { readonly level: number; readonly cost: number; readonly primaryNow: number; readonly primaryNext: number; readonly powerNow: number; readonly powerNext: number } | null;
  readonly essence: number;
  /** Essence missing for the next level (0: affordable). */
  readonly short: number;
  /** Raise max: the level the essence held reaches and what it costs (null when it is no more than one level). */
  readonly max: { readonly to: number; readonly cost: number } | null;
  /** Essence from here to the cap (the "to cap" hint). */
  readonly toCap: number;
  readonly bindsOnNext: boolean;
  /** Raise max would bind it (worn, not bound, its raised levels passing the threshold) though one level would not. */
  readonly maxBinds: boolean;
}

export function raiseBox(profile: PlayerProfile, view: ItemCardView): RaiseBox {
  const B = profile.balance, r = view.raise, it = view.item;
  const owner = capOwner(profile, it);
  const atCap = view.level >= view.cap;
  const capLine = atCap ? `Max for now · raise ${owner.name}` : `Rises to Lv ${view.cap} with ${owner.name}`;
  const max = r.affordableLevel > view.level + 1 ? { to: r.affordableLevel, cost: raiseCostRange(B, it.rarity, view.level, r.affordableLevel) } : null;
  return {
    level: view.level, cap: view.cap, fill: view.cap <= 1 ? 1 : Math.max(0, Math.min(1, (view.level - 1) / (view.cap - 1))), atCap, capLine, capTower: owner.towerIndex,
    next: atCap ? null : { level: view.level + 1, cost: r.nextCost, primaryNow: r.primaryNow.toNumber(), primaryNext: r.primaryNext.toNumber(), powerNow: r.powerNow, powerNext: r.powerNext },
    essence: view.essence, short: atCap ? 0 : Math.max(0, r.nextCost - view.essence),
    max, toCap: r.costToCap, bindsOnNext: r.bindsOnNext,
    maxBinds: max !== null && !r.bindsOnNext && it.equippedTo !== null && !it.bound && it.raised + (max.to - view.level) > B.gear.bindAboveRaised,
  };
}

// ------------------------------------------------------------------------------------------------------ fusion

/** One line of fusion readiness on the item card: what it would make and whether it can now. */
export interface FusionLine {
  readonly state: "ready" | "short" | "top";
  /** "3 Rare → 1 Epic: 2 spares ready", "Needs 1 more Rare tower item (or a Rare sigil)", "Mythic is the top". */
  readonly text: string;
  readonly result: Rarity | null;
}

export const kindWord = (B: BalanceData, it: GearItem): string => (B.gear.slots[it.slot].target === GearTarget.Base ? "fortress" : "tower");

export function fusionLine(profile: PlayerProfile, view: ItemCardView): FusionLine {
  const B = profile.balance, it = view.item;
  if (view.fusion === null) return { state: "top", text: "Mythic is the top rarity: it never fuses", result: null };
  const next = (it.rarity + 1) as Rarity, word = RARITY_LABEL[it.rarity];
  const usable = Math.min(view.fusion.sigils, B.gear.fusionMaxSigils);
  if (view.fusion.ready) {
    const parts = [view.fusion.fodder > 0 ? `${Math.min(2, view.fusion.fodder)} spare${Math.min(2, view.fusion.fodder) === 1 ? "" : "s"}` : "", usable > 0 && view.fusion.fodder < 2 ? `${Math.min(usable, 2 - view.fusion.fodder)} sigil${Math.min(usable, 2 - view.fusion.fodder) === 1 ? "" : "s"}` : ""].filter(Boolean);
    return { state: "ready", text: `3 ${word} → 1 ${RARITY_LABEL[next]} · ready with ${parts.join(" + ")}`, result: next };
  }
  const need = 2 - view.fusion.fodder - usable;
  return { state: "short", text: `3 ${word} → 1 ${RARITY_LABEL[next]} · needs ${need} more spare ${word} ${kindWord(B, it)} item${need === 1 ? "" : "s"} or ${word} sigil${need === 1 ? "" : "s"}`, result: next };
}

/** The Fusion screen's choice: the keeper, the fodder picked, sigils standing in, and (to Mythic) the effect. */
export interface FusionDraft {
  readonly keeper: number;
  readonly fodder: readonly number[];
  readonly sigils: number;
  readonly mythic: number | null;
}

/** The suggested draft (the two least-missed spares, sigils for what is short), or an empty one to pick into. */
export function initialDraft(c: FusionCandidates): FusionDraft {
  const s = c.suggested;
  return s ? { keeper: s.keeper, fodder: [...s.fodder], sigils: s.sigils, mythic: c.mythicChoices[0]?.index ?? null } : { keeper: c.keeper.id, fodder: [], sigils: 0, mythic: c.mythicChoices[0]?.index ?? null };
}

/** Taps a fodder tile: in or out; a third pick swaps out the oldest; picks + sigils never pass two. */
export function toggleFodder(d: FusionDraft, id: number): FusionDraft {
  if (d.fodder.includes(id)) return { ...d, fodder: d.fodder.filter((x) => x !== id) };
  const fodder = [...d.fodder, id].slice(-2);
  return { ...d, fodder, sigils: Math.min(d.sigils, 2 - fodder.length) };
}

/** Sets the sigils standing in (0…2, the held stock and the rule's cap); an item is dropped when they would pass two. */
export function withSigils(d: FusionDraft, n: number, c: FusionCandidates): FusionDraft {
  const sigils = Math.max(0, Math.min(n, c.sigilsHeld, c.maxSigils, 2));
  return { ...d, sigils, fodder: d.fodder.slice(0, 2 - sigils) };
}

export const draftRequest = (d: FusionDraft): FusionRequest => ({ keeper: d.keeper, fodder: [...d.fodder], sigils: d.sigils, ...(d.mythic !== null ? { mythicEffect: d.mythic } : {}) });
export const draftFull = (d: FusionDraft): boolean => d.fodder.length + d.sigils === 2;

/** The draft's preview (the exact result) once it is full, or why not (a string), or null while picking. */
export function draftPreview(profile: PlayerProfile, d: FusionDraft): FusionPreview | string | null {
  return draftFull(d) ? fusionPreview(profile, draftRequest(d)) : null;
}

/** The essence the fusion leaves: held + what comes back − the fee (negative: short). */
export function essenceAfter(profile: PlayerProfile, p: FusionPreview): number { return profile.forge.essence + p.plan.essenceBack - p.plan.fee; }

/** The FUSE badge: groups the stash could fuse now, for the button's count and the keeper picker's sections. */
export function fuseBadge(profile: PlayerProfile): { readonly groups: readonly FusableGroup[]; readonly count: number } {
  const groups = fusableGroups(profile);
  return { groups, count: groups.length };
}

/** A fusable group's keepers: every item of the rarity and kind (worn and locked ones too), the best first. */
export function groupKeepers(profile: PlayerProfile, g: FusableGroup): GearItem[] {
  const B = profile.balance;
  return profile.gear.filter((it) => it.rarity === g.rarity && B.gear.slots[it.slot].target === g.kind)
    .sort((a, b) => Number(b.equippedTo !== null) - Number(a.equippedTo !== null) || b.level - a.level || a.id - b.id);
}

// -------------------------------------------------------------------------------------------------------- feed

/**
 * Feed's default picks: the candidates (junk first, never worn / locked / NEW / Mythic, Epic+ only when confirmed) in
 * order, as many as still raise the target — it stops once the target would reach its cap, and picks none when the
 * essence held already gets it there.
 */
export function feedDefaults(profile: PlayerProfile, target: GearItem, opts: { readonly roster?: readonly boolean[]; readonly seenUpTo?: number; readonly confirmEpic?: boolean } = {}): number[] {
  const cands = feedCandidates(profile, target, opts);
  const view = itemCard(profile, target), cap = view.cap;
  // Nothing to melt when the essence held already takes it to its cap (Raise max does it).
  if (target.level >= cap || view.raise.affordableLevel >= cap) return [];
  const picked: number[] = [];
  for (const c of cands) {
    picked.push(c.id);
    const p = feedPreview(profile, target.id, picked, opts.confirmEpic === true);
    if (typeof p !== "string" && p.toLevel >= cap) break;
  }
  return picked;
}
